import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Users, Globe, RefreshCw, AtSign, BarChart3, MapPin, User2, TrendingUp,
} from "lucide-react";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const PLATFORM_ICONS: Record<string, React.ElementType> = {
  instagram: Instagram, twitter: Twitter, linkedin: Linkedin, facebook: Facebook, threads: AtSign,
};

interface DemographicBreakdown { label: string; value: number; }

export function AudienceDemographicsView() {
  const { orgId } = useOrgPermissions();
  const { toast } = useToast();
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [syncing, setSyncing] = useState(false);

  const { data: accounts = [] } = useQuery({
    queryKey: ["demo-accounts", orgId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      let q = supabase.from("social_accounts")
        .select("id,platform,account_handle,account_name,follower_count,access_token,account_id_external")
        .eq("user_id", user.id).eq("is_connected", true);
      if (orgId) q = (q as any).eq("org_id", orgId);
      const { data } = await (q as any);
      const result = (data ?? []) as any[];
      if (result.length > 0 && !selectedAccount) setSelectedAccount(result[0].id);
      return result;
    },
  });

  const { data: demographics = [], refetch } = useQuery({
    queryKey: ["audience-demographics", selectedAccount],
    enabled: !!selectedAccount,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("audience_demographics")
        .select("*")
        .eq("social_account_id", selectedAccount);
      return (data ?? []) as any[];
    },
  });

  // For non-Instagram platforms, read from account_insights
  const { data: insightRow, refetch: refetchInsights } = useQuery({
    queryKey: ["account-insights-demo", selectedAccount],
    enabled: !!selectedAccount,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("account_insights")
        .select("*")
        .eq("social_account_id", selectedAccount)
        .order("snapshot_date", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data ?? null;
    },
  });

  const account = accounts.find((a: any) => a.id === selectedAccount);

  const handleSync = async () => {
    if (!account) return;
    setSyncing(true);
    try {
      if (account.platform === "instagram") {
        // Instagram Insights: audience_gender_age, audience_country, audience_city
        const fields = ["audience_gender_age", "audience_country", "audience_city"];
        for (const metric of fields) {
          const res = await fetch(
            `https://graph.instagram.com/v21.0/${account.account_id_external}/insights?metric=${metric}&period=lifetime&access_token=${account.access_token}`
          );
          if (!res.ok) { console.warn(`Instagram ${metric} failed:`, await res.text()); continue; }
          const { data: insightData } = await res.json();
          const raw = insightData?.[0]?.values?.[0]?.value ?? insightData?.[0]?.value ?? {};

          let breakdown: DemographicBreakdown[] = [];

          if (metric === "audience_gender_age") {
            // e.g. { "F.18-24": 0.12, "M.25-34": 0.35, ... }
            const ageBuckets: Record<string, number> = {};
            const genderBuckets: Record<string, number> = {};
            let total = Object.values(raw as Record<string,number>).reduce((s, v) => s + v, 0);
            if (!total) total = 1;
            for (const [key, val] of Object.entries(raw as Record<string,number>)) {
              const [gender, age] = key.split(".");
              ageBuckets[age]    = (ageBuckets[age]    ?? 0) + val;
              genderBuckets[gender === "F" ? "Female" : gender === "M" ? "Male" : "Other"] =
                (genderBuckets[gender === "F" ? "Female" : gender === "M" ? "Male" : "Other"] ?? 0) + val;
            }
            // Upsert age
            await (supabase as any).from("audience_demographics").upsert({
              social_account_id: selectedAccount,
              metric_type: "age",
              breakdown: Object.entries(ageBuckets).map(([label, v]) => ({ label, value: Math.round((v / total) * 100) })).sort((a, b) => a.label.localeCompare(b.label)),
              synced_at: new Date().toISOString(),
            }, { onConflict: "social_account_id,metric_type" });
            // Upsert gender
            await (supabase as any).from("audience_demographics").upsert({
              social_account_id: selectedAccount,
              metric_type: "gender",
              breakdown: Object.entries(genderBuckets).map(([label, v]) => ({ label, value: Math.round((v / total) * 100) })),
              synced_at: new Date().toISOString(),
            }, { onConflict: "social_account_id,metric_type" });
          } else {
            // country or city: { "US": 1234, "GB": 567, ... }
            const total = Object.values(raw as Record<string,number>).reduce((s, v) => s + v, 0) || 1;
            breakdown = Object.entries(raw as Record<string,number>)
              .map(([label, v]) => ({ label, value: Math.round((v / total) * 100) }))
              .sort((a, b) => b.value - a.value)
              .slice(0, 15);
            await (supabase as any).from("audience_demographics").upsert({
              social_account_id: selectedAccount,
              metric_type: metric === "audience_country" ? "country" : "city",
              breakdown,
              synced_at: new Date().toISOString(),
            }, { onConflict: "social_account_id,metric_type" });
          }
        }
      } else {
        // For Facebook, LinkedIn, YouTube, TikTok — call fetch-account-insights
        const { error } = await supabase.functions.invoke("fetch-account-insights", {
          body: { socialAccountId: selectedAccount },
        });
        if (error) throw new Error(error.message);
        refetchInsights();
      }
      refetch();
      toast({ title: "Demographics synced" });
    } catch (e: any) {
      toast({ title: "Sync failed", description: e.message, variant: "destructive" });
    } finally {
      setSyncing(false);
    }
  };

  const isInstagram = account?.platform === "instagram";

  const getDemoByType = (type: string) =>
    demographics.find((d: any) => d.metric_type === type)?.breakdown ?? [];

  // For non-Instagram: map account_insights JSON columns into DemographicBreakdown[]
  const toBreakdown = (raw: any): DemographicBreakdown[] => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw as DemographicBreakdown[];
    // Object form: { "US": 1234, "GB": 567 }
    const total = Object.values(raw as Record<string, number>).reduce((s, v) => s + (v as number), 0) || 1;
    return Object.entries(raw as Record<string, number>)
      .map(([label, v]) => ({ label, value: Math.round(((v as number) / total) * 100) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);
  };

  const ageData:    DemographicBreakdown[] = isInstagram
    ? getDemoByType("age")
    : toBreakdown(insightRow?.audience_age);

  const genderData: DemographicBreakdown[] = isInstagram
    ? getDemoByType("gender")
    : toBreakdown(insightRow?.audience_gender);

  const countryData:DemographicBreakdown[] = isInstagram
    ? getDemoByType("country")
    : toBreakdown(insightRow?.audience_country);

  const cityData:   DemographicBreakdown[] = isInstagram
    ? getDemoByType("city")
    : toBreakdown(insightRow?.audience_city);

  const lastSynced = isInstagram
    ? demographics[0]?.synced_at
    : insightRow?.snapshot_date;

  const GENDER_COLORS: Record<string, string> = { Female: "#e1306c", Male: "#1da1f2", Other: "#6b7280" };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> Audience Demographics
          </h2>
          <p className="text-sm text-muted-foreground">Age, gender, location breakdown of your followers</p>
        </div>
        <div className="flex items-center gap-2">
          {accounts.length > 1 && (
            <Select value={selectedAccount} onValueChange={setSelectedAccount}>
              <SelectTrigger className="w-44 h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {accounts.map((a: any) => {
                  const Icon = PLATFORM_ICONS[a.platform] ?? AtSign;
                  return (
                    <SelectItem key={a.id} value={a.id}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5" />
                        <span className="capitalize">{a.platform}</span>
                        <span className="text-muted-foreground">@{a.account_handle}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
          <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing || !selectedAccount}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync Now"}
          </Button>
        </div>
      </div>

      {selectedAccount && account && (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          {(() => { const Icon = PLATFORM_ICONS[account.platform] ?? AtSign; return <Icon className="w-4 h-4" />; })()}
          <span className="capitalize font-medium">{account.platform}</span>
          <span>@{account.account_handle}</span>
          {lastSynced && <span className="ml-2">· Synced {formatDistanceToNow(new Date(lastSynced), { addSuffix: true })}</span>}
          {["twitter", "tiktok", "threads", "pinterest"].includes(account?.platform ?? "") && (
            <Badge variant="secondary" className="ml-2 text-[10px]">Limited demographics on {account?.platform}</Badge>
          )}
        </div>
      )}

      {ageData.length === 0 && genderData.length === 0 && countryData.length === 0 && !syncing ? (
        <Card className="p-10 text-center">
          <BarChart3 className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
          <p className="font-medium text-muted-foreground">No demographic data yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
            Click "Sync Now" to pull audience demographics.
            {isInstagram ? " Requires an Instagram Business or Creator account." : ""}
          </p>
          <Button className="mt-4" size="sm" onClick={handleSync} disabled={syncing || !selectedAccount}>
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sync Demographics
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Age breakdown */}
          {ageData.length > 0 && (
            <Card className="p-5">
              <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
                <User2 className="w-4 h-4 text-primary" /> Age Breakdown
              </h3>
              <div className="space-y-2.5">
                {ageData.map((d: DemographicBreakdown) => (
                  <div key={d.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium">{d.label}</span>
                      <span className="text-muted-foreground">{d.value}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${d.value}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Gender breakdown */}
          {genderData.length > 0 && (
            <Card className="p-5">
              <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" /> Gender Breakdown
              </h3>
              <div className="space-y-4">
                {/* Donut-like visual */}
                <div className="flex gap-4 justify-center">
                  {genderData.map((d: DemographicBreakdown) => (
                    <div key={d.label} className="text-center">
                      <div
                        className="w-20 h-20 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto shadow-md"
                        style={{ background: GENDER_COLORS[d.label] ?? "#888" }}
                      >
                        {d.value}%
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">{d.label}</p>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  {genderData.map((d: DemographicBreakdown) => (
                    <div key={d.label}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium">{d.label}</span>
                        <span className="text-muted-foreground">{d.value}%</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${d.value}%`, background: GENDER_COLORS[d.label] ?? "#888" }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Top countries */}
          {countryData.length > 0 && (
            <Card className="p-5">
              <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
                <Globe className="w-4 h-4 text-primary" /> Top Countries
              </h3>
              <div className="space-y-2">
                {countryData.slice(0, 10).map((d: DemographicBreakdown) => (
                  <div key={d.label} className="flex items-center gap-3">
                    <span className="text-xs font-medium w-8 shrink-0">{d.label}</span>
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${d.value}%` }} />
                    </div>
                    <span className="text-xs text-muted-foreground w-8 text-right shrink-0">{d.value}%</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Top cities */}
          {cityData.length > 0 && (
            <Card className="p-5">
              <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" /> Top Cities
              </h3>
              <div className="space-y-2">
                {cityData.slice(0, 10).map((d: DemographicBreakdown) => (
                  <div key={d.label} className="flex items-center gap-3">
                    <span className="text-xs font-medium flex-1 truncate">{d.label}</span>
                    <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${d.value}%` }} />
                    </div>
                    <span className="text-xs text-muted-foreground w-8 text-right shrink-0">{d.value}%</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
